import { VenueRentalConfigurationType } from '@api';
import { FormActions, VersionFormFields } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckboxField,
  NumberField,
  SelectField,
  TextareaField,
} from '@hypeit-gmbh/hypeit-ui';
import { Grid, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useConfigurationData,
  useRentalConfigurationFormSchema,
  useVersionData,
} from './hooks';
import { RentalConfigurationFormValuesType } from './types';

const TRANS_PATH = 'RENTAL_CONFIGURATION_TABLE.FORM';

type RentalConfigurationFormPropsType = {
  defaultRentalConfiguration?: VenueRentalConfigurationType;
  currentVersion: string;
  onCancel: () => void;
  onSave: (data: RentalConfigurationFormValuesType) => void;
};

export const RentalConfigurationForm: React.FC<
  RentalConfigurationFormPropsType
> = ({ defaultRentalConfiguration, currentVersion, onCancel, onSave }) => {
  const { t } = useTranslation('venueDetailsRental');
  const { schema, defaultValues } = useRentalConfigurationFormSchema({
    defaultRentalConfiguration,
    currentVersion,
  });
  const { configurationData } = useConfigurationData();
  const { versionData } = useVersionData();

  const methods = useForm<RentalConfigurationFormValuesType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSave)}>
        <Stack gap="md">
          <VersionFormFields />
          <Grid gap="md">
            <Grid.Col span={6}>
              <Stack gap="md">
                <SelectField
                  fieldName="configuration"
                  label={t(`${TRANS_PATH}.CONFIGURATION_LABEL`)}
                  data={configurationData}
                  required
                  searchable
                />
                <NumberField
                  fieldName="capacity"
                  label={t(`${TRANS_PATH}.CAPACITY_LABEL`)}
                />
                <NumberField
                  fieldName="rentalPercentageNet"
                  label={t(`${TRANS_PATH}.RENTAL_PERCENTAGE_NET_LABEL`)}
                  suffix="%"
                />
                <NumberField
                  fieldName="rentalPercentageGross"
                  label={t(`${TRANS_PATH}.RENTAL_PERCENTAGE_GROSS_LABEL`)}
                  suffix="%"
                />
                <NumberField
                  fieldName="rentalFlatFee"
                  label={t(`${TRANS_PATH}.RENTAL_FLAT_FEE_LABEL`)}
                  suffix="€"
                />
                <NumberField
                  fieldName="minimumRental"
                  label={t(`${TRANS_PATH}.MINIMUM_RENTAL_LABEL`)}
                />
              </Stack>
            </Grid.Col>
            <Grid.Col span={6}>
              <Stack gap="md">
                <SelectField
                  fieldName="version"
                  label={t(`${TRANS_PATH}.VERSION_LABEL`)}
                  data={versionData}
                  searchable
                />
                <NumberField
                  fieldName="fixedVenueSideCosts"
                  label={t(`${TRANS_PATH}.FIXED_VENUE_SIDE_COSTS_LABEL`)}
                  suffix="€"
                />
                <NumberField
                  fieldName="rentalSharePerTicket"
                  label={t(`${TRANS_PATH}.RENTAL_SHARE_PER_TICKET_LABEL`)}
                  suffix="€"
                />
                <NumberField
                  fieldName="rentalShareBasis"
                  label={t(`${TRANS_PATH}.RENTAL_SHARE_BASIS_LABEL`)}
                />
                <NumberField
                  fieldName="maximumRental"
                  label={t(`${TRANS_PATH}.MAXIMUM_RENTAL_LABEL`)}
                  suffix="€"
                />
              </Stack>
            </Grid.Col>
            <Grid.Col span={12}>
              <Stack gap="md">
                <TextareaField
                  fieldName="notes"
                  label={t(`${TRANS_PATH}.NOTES_LABEL`)}
                />
                <CheckboxField
                  fieldName="archived"
                  label={t(`${TRANS_PATH}.ARCHIVED_LABEL`)}
                />
              </Stack>
            </Grid.Col>
          </Grid>
          <FormActions
            onCancel={onCancel}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
